import {Component, OnInit, signal} from '@angular/core';
import {FinancialService} from '../services/financial.service';
import {Utils} from '../utils.class';
import {AfasService} from '../services/afas.service';
import {combineLatest} from 'rxjs';
import {AfasInvoice} from "../classes/invoice";
import {User} from "../classes/user.class";
import {UserService} from "../services/user/user.service";
import {CodaltComponent} from '../codalt.component';
import {CurrencyPipe, DatePipe, DecimalPipe} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {FilterPipe} from '../pipes/filter.pipe';
import {SumPipe} from '../pipes/sum.pipe';
import {Company} from '../classes/company';
import {RouterLink} from '@angular/router';
import {HasFunctionalityPipe} from '../pipes/has-functionality.pipe';
import {Title} from '@angular/platform-browser';

@Component({
    selector: 'app-financial',
    templateUrl: './financial.component.html',
    styleUrls: ['./financial.component.scss'],
    imports: [
        ReactiveFormsModule,
        FormsModule,
        CurrencyPipe,
        DatePipe,
        DecimalPipe,
        FilterPipe,
        SumPipe,
        RouterLink,
        HasFunctionalityPipe
    ]
})
export class FinancialComponent extends CodaltComponent implements OnInit {

    readonly hourlyCompanies = signal<Company[]>(null);
    readonly hourlyEmployees = signal<User[]>(null);
    readonly invoices = signal<AfasInvoice[]>(null);
    showSubs = new FormControl<boolean>(false);
    readonly users = signal<User[]>(null);
    hourlyRate = 100;

    utils = Utils;
    readonly hasInvoice = signal<null | number[]>([]);
    invoicing: number;
    invoicedates: string[] = [];

    currentYear: number;
    currentPeriod: number;
    previousPeriod: number;

    constructor(
        private afasService: AfasService,
        private financialService: FinancialService,
        private titleService: Title,
        private userService: UserService,
    ) {
        super();
        this.titleService.setTitle(`Financials ${this.environment.titleAppend}`);
    }

    get generalCosts(): number {
        if (!this.filteredUsers) return 0;
        const totalEmployeeCosts = Utils.sumProps(this.filteredUsers, 'period_costs');
        return totalEmployeeCosts * 0.1;
    }

    get daysElapsedInPeriod(): number {
        const today = new Date();

        // Get ISO week number (1-53)
        const getISOWeek = (date: Date): number => {
            const tempDate = new Date(date.getTime());
            tempDate.setHours(0, 0, 0, 0);
            tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
            const week1 = new Date(tempDate.getFullYear(), 0, 4);
            return 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
        };

        const isoWeek = getISOWeek(today);

        // Calculate end of current period
        const endOfPeriod = new Date(today);
        const weeksToAdd = (4 - (isoWeek % 4)) % 4;
        endOfPeriod.setDate(endOfPeriod.getDate() + (weeksToAdd * 7));

        // Go to end of week (Sunday)
        const dayOfWeek = endOfPeriod.getDay();
        const daysToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
        endOfPeriod.setDate(endOfPeriod.getDate() + daysToSunday);
        endOfPeriod.setHours(23, 59, 59, 999);

        // Start of period is 4 weeks before end + 1 day
        const startOfPeriod = new Date(endOfPeriod);
        startOfPeriod.setDate(startOfPeriod.getDate() - (4 * 7) + 1);
        startOfPeriod.setHours(0, 0, 0, 0);

        // Calculate working days elapsed (Monday to Friday only)
        let daysElapsed = 0;
        const currentDate = new Date(startOfPeriod);

        while (currentDate <= today) {
            const dayOfWeek = currentDate.getDay();
            // Count Monday(1) to Friday(5) as working days
            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                daysElapsed++;
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return daysElapsed;
    }

    get filteredUsers(): User[] {
        if (!this.users()) return [];
        const today = new Date();
        const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        return this.users().filter(user => {
            const start = user['employment_start'] ? new Date(user['employment_start']) : null;
            const end = user['employment_end'] ? new Date(user['employment_end']) : null;
            const startOk = !start || start <= today;
            const endOk = !end || end > threeMonthsAgo;
            return startOk && endOk;
        });
    }

    ngOnInit() {
        this.subscriptions.add(
            this.financialService.getHourlyEmployees().subscribe((employees) => {
                this.hourlyEmployees.set(employees);
                this.subscriptions.add(
                    this.userService.getList().subscribe((users) => {
                        this.users.set(users);
                        this.users().forEach(user => {
                            const emp = this.hourlyEmployees().find(e => e.id === user.id);
                            if (emp) {
                                user['prev_month'] = emp['prevmonth'];
                                user['cur_month'] = emp['curmonth'];
                            }
                        });
                        this.applyPCosts(this.users());
                    })
                );
            })
        );
        this.load();

        const isoPeriod = this.currentIsoPeriod();
        this.currentYear = isoPeriod.year;
        this.currentPeriod = isoPeriod.period;
        this.previousPeriod = isoPeriod.period === 1 ? 13 : isoPeriod.period - 1;
    }

    private applyPCosts(users: User[]): void {
        const pcosts: { [userId: number]: number } = JSON.parse(localStorage.getItem('pcosts') ?? '{}') ?? {};
        users.forEach(user => pcosts[user.id] ??= 123);
        localStorage.setItem('pcosts', JSON.stringify(pcosts));
        users.forEach(user => user['period_costs'] = pcosts[user.id] / 13);
    }

    load() {
        const companies$ = this.financialService.getHourlyCompanies();
        const invoices$ = this.afasService.getInvoices();

        this.subscriptions.add(
            combineLatest([companies$, invoices$]).subscribe(([companies, invoices]) => {
                this.hourlyCompanies.set(companies);
                this.invoices.set(invoices);
                this.invoicedates = Array.from(new Set(invoices?.map(i => i.nookPieceDate)));
                if ((+(new Date()).getDate()) < 20) {
                    this.invoices()?.forEach(invoice => {
                        const invoiceMonth = +(new Date(invoice.nookPieceDate)).getMonth();
                        const currentMonth = +(new Date()).getMonth();
                        if (invoiceMonth == currentMonth) {
                            this.hourlyCompanies().forEach(company => {
                                // Support both Exact and AFAS during migration
                                if (company.afas_debtor_id == invoice.debtorId) {
                                    this.hasInvoice().push(company.id);
                                }
                            })
                        }
                    });
                }
            })
        );
    }

    invoiceMonth(companyId: number) {
        this.invoicing = companyId;
        this.subscriptions.add(
            this.afasService.invoiceMonth(companyId).subscribe(() => {
                this.hasInvoice().push(companyId);
                this.load();
                this.invoicing = null;
            }, () => {
                this.invoicing = null;
            })
        );
    }

    private currentIsoPeriod(): { year: number, period: number } {
        const today = new Date();
        const tmp = new Date(today);
        tmp.setDate(tmp.getDate() + 4 - (tmp.getDay() || 7));
        const year = tmp.getFullYear();
        const yearStart = new Date(year, 0, 4);
        yearStart.setDate(yearStart.getDate() - (yearStart.getDay() || 7) + 1);
        const daysSinceStart = Math.floor((today.getTime() - yearStart.getTime()) / 86400000);
        return {year, period: Math.min(Math.floor(daysSinceStart / 28) + 1, 13)};
    }
}
